import {Trans} from "@lingui/react/macro";
import {Link, Navigate} from "react-router-dom";
import AddGroupForm from "../features/group/AddGroupForm";
import type {GroupId} from "../models/GroupRepo";
import type {UserRepo} from "../models/UserRepo";
import AppShell from "../shared/ui/AppShell";
import Header from "../shared/ui/Header";
import {StorageKey, storageWrapper} from "../storage";
import {Box, Col, css, Text} from "../ui/Box";
import Button from "../ui/Button";
import Icon from "../ui/Icon";
import NotFound from "./NotFound";

const GroupLink = ({id, userRepo}: {id: GroupId; userRepo: UserRepo}) => {
  const group = userRepo.Group.useById(id);
  if (!group)
    return (
      <div>
        <Trans>Group not found</Trans>
      </div>
    );
  return (
    <Link
      to={`/g/${group.shortId}`}
      className={css({
        display: "flex",
        sp: "12",
        align: "baseline",
        rounded: "4",
        bg: "surface",
        pa: "16",
      })}
    >
      <Box color="primary" textTransform="uppercase" size="16" bold>
        {group.name}
      </Box>
      <Text color="secondary" size="14_tight">
        {group.joinedAt.toDateString()}
      </Text>
    </Link>
  );
};

const RedirectToLastGroup = ({id, userRepo}: {id: GroupId; userRepo: UserRepo}) => {
  const group = userRepo.Group.useById(id);
  if (group) {
    return <Navigate to={`/g/${group.shortId}`} />;
  } else {
    return <NotFound />;
  }
};

const Hub = ({userRepo}: {userRepo: UserRepo}) => {
  const groupIds = userRepo.Group.useGetAllIds();
  const lastGroupId = storageWrapper.storageGet(StorageKey.LAST_GROUP_ID);
  const validGroupId = lastGroupId && groupIds.includes(lastGroupId) ? lastGroupId : null;

  if (validGroupId) {
    return <RedirectToLastGroup id={validGroupId} userRepo={userRepo} />;
  } else {
    return (
      <AppShell
        header={
          <Header
            title={<Trans id="nav.lists">Lists</Trans>}
            actions={
              <Button variant="tertiary" to="/profile">
                <Icon name="cog" />
              </Button>
            }
          />
        }
      >
        <Col px="12" sp="24" py="24">
          {groupIds.length === 0 ? (
            <Text>
              <Trans>No Groups</Trans>
            </Text>
          ) : (
            groupIds.map((id) => <GroupLink key={id} id={id} userRepo={userRepo} />)
          )}
          <Col rounded="4" surface pa="16" sp="12">
            <Text as="h2" size="20">
              <Trans>Create New Group</Trans>
            </Text>
            <AddGroupForm userRepo={userRepo} />
          </Col>
        </Col>
      </AppShell>
    );
  }
};
export default Hub;
